import Loading from 'components/Loading';
import Text from 'components/Text';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useUserStore } from 'store';
import MessageItem from '../MessageItem';
import MediaLightbox, {
  TLightboxMedia,
} from '../../MediaLightbox';
import { TEXT_STRING } from '../../../constants';
import moment from 'moment';
import 'moment/locale/ja';
import {
  TChannelDetail,
  TMessageDetail,
} from 'types';
import { useCallback, useState } from 'react';
import { usePubNub } from 'pubnub-react';

type Props = {
  isLoading?: boolean;
  messages: TMessageDetail[];
  fetchMore: () => void;
  hasMore: boolean;
  channelData?: TChannelDetail;
  onDeleteRequest?: (timetoken: string) => void;
};

const MessageList = ({
  isLoading,
  messages,
  fetchMore,
  hasMore,
  onDeleteRequest,
}: Props) => {
  const { user, clientId } = useUserStore();
  const pubnub = usePubNub();
  const [lightboxMedia, setLightboxMedia] = useState<TLightboxMedia | null>(
    null,
  );

  // Get current PubNub UUID - this is what we use to identify our messages
  // Priority: user?.uuid (from backend) > pubnub?.getUUID() > clientId
  // This ensures we use the correct UUID even if PubNub was initialized with clientId before user was loaded
  const currentUuid = user?.uuid || pubnub?.getUUID() || clientId;

  const handleMessageTime = useCallback(
    (msgTimeToken: string, beforeMsgToken?: string) => {
      const msgTime = +msgTimeToken / 10000;
      const beforeMsgTime = +(beforeMsgToken || 0) / 10000;
      let displayTime;
      const isOneHourDiff = moment(msgTime).locale('ja').diff(beforeMsgTime, 'hours') > 1;
      if (
        !moment(msgTime).locale('ja').isSame(beforeMsgTime, 'day') ||
        isNaN(beforeMsgTime)
      ) {
        displayTime = moment(msgTime).locale('ja').format('ll (dd) HH:mm');
      } else if (isOneHourDiff) {
        displayTime = moment(msgTime).locale('ja').format('HH:mm');
      }
      return { displayTime, isOneHourDiff };
    },
    []
  );

  const isMyMessage = useCallback(
    (publisher: string) => currentUuid === publisher,
    [currentUuid]
  );

  return (
    <div
      id="scrollableMessage"
      className="h-full gap-4 overflow-y-auto p-4 flex flex-col-reverse"
    >
      {isLoading ? (
        <div className="m-auto">
          <Loading />
        </div>
      ) : !messages.length ? (
        <Text className="m-auto tracking-widest" center bold>
          {TEXT_STRING.MESSAGE.START_MESSAGE}
        </Text>
      ) : (
        <>
          <InfiniteScroll
            next={fetchMore}
            inverse={true}
            hasMore={hasMore}
            loader={
              <div className="py-[36px] flex justify-center items-center">
                <Loading />
              </div>
            }
            dataLength={messages.length}
            scrollThreshold={0.5}
            className="gap-4 flex flex-col-reverse overflow-hidden"
            scrollableTarget="scrollableMessage"
          >
            {messages.map((item, index, arr) => {
              const { displayTime } = handleMessageTime(
                item.timetoken,
                arr[index + 1]?.timetoken
              );
              const myMessage = isMyMessage(item.uuid);
              return (
                <div key={item.timetoken} className="overflow-hidden">
                  {displayTime && (
                    <Text center className="mb-4">
                      {displayTime}
                    </Text>
                  )}
                  <MessageItem
                    message={item}
                    isMyMessage={myMessage}
                    onDeleteRequest={myMessage ? onDeleteRequest : undefined}
                    onMediaClick={setLightboxMedia}
                  />
                </div>
              );
            })}
          </InfiniteScroll>
        </>
      )}
      <MediaLightbox
        media={lightboxMedia}
        onClose={() => setLightboxMedia(null)}
      />
    </div>
  );
};

export default MessageList;
